import { Attendee } from './types';
import { DOB_REGEX } from './age';

// Helpers for the Birthdays page. Pure functions only — no Firestore, no
// React. Birthdates are stored as `mm/dd/yyyy` strings (see
// `Attendee.birthdate` in `types.ts`); we reuse `DOB_REGEX` from `age.ts`
// rather than re-deriving it.

export type BirthdayMonth = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

export const MONTH_NAMES: readonly string[] = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

// A row of decoded DOB info, ready to render. `attendee` keeps the original
// document so the page can reach into name / grade / groupRef without a
// second lookup.
export type BirthdayRow = {
  attendee: Attendee;
  month: BirthdayMonth;
  day: number;
  year: number;
  // Age the attendee will turn during the calendar year `referenceYear`
  // passed into `getBirthdaysInMonth`. Computed as `referenceYear - year`,
  // mirroring how the standalone `birthdays-by-month.mjs` script frames it
  // (this is "the age they will turn this year", not their age today).
  ageTurning: number;
};

// Parses a `mm/dd/yyyy` string into its parts. Returns null when the input
// is missing, empty, or doesn't match the regex (same shape-only validation
// as `ageFromDob`).
export const parseDob = (
  dob?: string | null,
): { month: BirthdayMonth; day: number; year: number } | null => {
  if (!dob) return null;
  const match = DOB_REGEX.exec(dob.trim());
  if (!match) return null;
  const [, m, d, y] = match;
  return {
    month: Number(m) as BirthdayMonth,
    day: Number(d),
    year: Number(y),
  };
};

// Filters attendees down to those whose birthdate falls in the given month
// and decorates each with day / year / ageTurning. Sorts by day ascending,
// then by `last, first` for deterministic ties. Attendees with missing or
// malformed birthdates are silently dropped — the page surfaces the
// "missing DOB" count separately so volunteers know data isn't being lost.
export const getBirthdaysInMonth = (
  attendees: Attendee[],
  month: BirthdayMonth,
  referenceYear: number,
): BirthdayRow[] => {
  const rows: BirthdayRow[] = [];
  for (const attendee of attendees) {
    const parsed = parseDob(attendee.birthdate);
    if (!parsed || parsed.month !== month) continue;
    rows.push({
      attendee,
      month: parsed.month,
      day: parsed.day,
      year: parsed.year,
      ageTurning: referenceYear - parsed.year,
    });
  }
  rows.sort((a, b) => {
    if (a.day !== b.day) return a.day - b.day;
    const aKey = `${a.attendee.last_name || ''} ${a.attendee.first_name || ''}`.toLowerCase();
    const bKey = `${b.attendee.last_name || ''} ${b.attendee.first_name || ''}`.toLowerCase();
    return aKey.localeCompare(bKey);
  });
  return rows;
};

// Counts attendees who have a non-empty `birthdate` that fails to parse.
// Surfaced on the Birthdays page so admins can spot bad data — it's not
// shown anywhere else (the Roster's `formatDobWithAge` quietly falls back
// to the raw string).
export const countMalformedBirthdates = (attendees: Attendee[]): number => {
  let count = 0;
  for (const a of attendees) {
    if (!a.birthdate) continue;
    if (!DOB_REGEX.test(a.birthdate.trim())) count += 1;
  }
  return count;
};

// Default month for the page: the active event's start month, falling back
// to the current calendar month when no `startDate` is set on the event
// (or when there is no active event at all). Returned as a 1-based month
// so it slots straight into `getBirthdaysInMonth`.
export const defaultBirthdayMonth = (
  eventStart: Date | undefined | null,
  today: Date = new Date(),
): BirthdayMonth => {
  if (eventStart && !Number.isNaN(eventStart.getTime())) {
    return (eventStart.getMonth() + 1) as BirthdayMonth;
  }
  return (today.getMonth() + 1) as BirthdayMonth;
};
